import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { withRouter } from 'react-router-dom'
import { Button, Card, Page, TextStyle } from '@superops/ui-design-system'
import {
  GET_PRODUCT_FEATURES,
  GET_ALL_PAYMENT_CURRENCIES,
  GET_PLANS,
  AccountClient,
  GET_ALL_ACTIVE_ADDONS,
  GET_PURCHASE_PRODUCT_FEATURE_META
} from '@nile/graphql'
import {
  getTranslatedText,
  CURRENCY_LIST,
  CURRENCY_SYMBOL_MAP,
  AppContext,
  GET_CONSTANTS,
  COMMON_CONSTANTS,
  useActivityTracker,
  useProduct
} from '@nile/components'
import {
  planDescriptionKeyMap,
  getFilteredCurrencies
} from '@nile/banyan/src/Utils/pricingHelper'
import { SVGIcon } from '@superops/super-icons'
import PricingContainer from '@nile/components/src/Containers/PricingContainer'
import { cloneDeep, isEmpty } from 'lodash'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  ModifyTechnician,
  ManageAssetModal
} from '@nile/banyan/src/Pages/Protected/Configuration/Pricing'
import PricingPageContainer from './PricingPageContainer'
import { PageWrapper, PlanWrapper } from './styles'
import PricingPageContext from './context/PricingPageContext'

import './common.scss'
import ItPlanPurchase from '@nile/banyan/src/Pages/Protected/Configuration/Pricing/ItPlanPurchase'

import '@superops/superops-pricing/msppricing/msppricing.css'
import '@superops/superops-pricing/itpricing/itpricing.css'

const SuperPricing = React.lazy(() =>
  import('@superops/superops-pricing').then((module) => ({
    default: module.MSPPricing // Map the named export to default
  }))
)

const ITPricing = React.lazy(() =>
  import('@superops/superops-pricing').then((module) => ({
    default: module.ITPricing // Map the named export to default
  }))
)

const PricingPage = () => {
  const flags = useFlags()
  return (
    <div>
      <PricingCard />
    </div>
  )
}

export const PricingCard = withRouter(
  ({ match, handleClose, isTrailExpired }) => {
    const intl = useIntl()

    const flags = useFlags()
    const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP)

    const appContext = useContext(AppContext)
    const { user, currentLanguage, companyInfo } = appContext
    const [selectedPlan, setSelectedPlan] = useState(null)
    const [isTieredModel, setIsTieredModel] = useState(false)
    const [currencyList, setCurrencyList] = useState([])
    const [currentCurrency, setCurrentCurrency] = useState(null)
    const [currentLocale, setCurrentLocale] = useState('en-US')
    const [tenure, setTenure] = useState('YEARLY')
    const [availablePlans, setAvailablePlans] = useState([])
    const [allFeatures, setAllFeatures] = useState([])
    const [pricingMeta, setPricingMeta] = useState([])
    const [addonPrices, setAddonPrices] = useState([])

    const monthlyPriceDescription = getTranslatedText(
      intl,
      'app.pricing.monthly.billingcycle'
    )
    const annualPriceDescription = getTranslatedText(
      intl,
      'app.pricing.annual.billingcycle'
    )

    const [openManageTechModal, setOpenManageTechModal] = useState(false)
    const [openManageAssetModal, setOpenManageAssetModal] = useState(false)

    const handleManageModal = useCallback((type) => {
      if (type === 'TECHNICIAN') {
        setOpenManageTechModal((prevState) => !prevState)
      }
      if (type === 'NETWORK_ASSET') {
        setOpenManageAssetModal((prevState) => !prevState)
      }
    }, [])

    const getAllPricingPlans = () => {
      AccountClient.query({ query: GET_PLANS }).then((res) => {
        if (res?.data?.getAllPlans) {
          const sortedPlan = flags?.priceSorting
            ? [...res.data.getAllPlans].sort(
                (a, b) => a.planOrder - b.planOrder
              )
            : res.data.getAllPlans
          setAvailablePlans(sortedPlan)
        }
      })
    }

    const getPricingMeta = () => {
      AccountClient.query({ query: GET_PURCHASE_PRODUCT_FEATURE_META }).then(
        (res) => {
          const result = res.data.getPurchaseProductFeatureMeta
          const data = isMSPProduct ? result.mspData : result.itData
          setPricingMeta(data)
        }
      )
    }

    const getAllAddons = () => {
      AccountClient.query({
        query: GET_ALL_ACTIVE_ADDONS,
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
      }).then((res) => {
        if (res?.data?.getAllActiveAddons) {
          setAddonPrices(res?.data?.getAllActiveAddons)
        }
      })
    }

    const getAllFeatures = () => {
      AccountClient.query({
        query: GET_PRODUCT_FEATURES,
        variables: {
          input: {
            includePurchaseOnlyFeatures: true
          }
        }
      }).then((res) => {
        if (res?.data?.getProductFeaturesCategories) {
          setAllFeatures(res.data.getProductFeaturesCategories)
        }
      })
    }
    const getCountryList = async () => {
      try {
        const country = await GET_CONSTANTS('country')
        return country
      } catch {
        // error
      }
    }
    const getCurrencies = () => {
      AccountClient.query({ query: GET_ALL_PAYMENT_CURRENCIES }).then(
        async (res) => {
          if (res?.data?.getAllPaymentCurrencies) {
            const countryList = await getCountryList()
            const filteredCurrency = getFilteredCurrencies(
              res?.data?.getAllPaymentCurrencies
            )
            const isCurrenyExist = filteredCurrency.some(
              (el) => el?.currencyCode === companyInfo?.currency
            )
            let isoCode = 'US'
            const updatedCurrencies = cloneDeep(filteredCurrency).map(
              (curr) => {
                const constructedData = { ...curr }
                constructedData.value = curr.currencyCode
                constructedData.label = (
                  <div className='flex'>
                    <TextStyle weight='Bold'>
                      {CURRENCY_SYMBOL_MAP[curr.currencyCode || 'USD']}
                    </TextStyle>
                    <div className='plr-5'>-</div>
                    <div>{curr.currencyCode || 'USD'}</div>
                    <div className='plr-5'>-</div>
                    <div>
                      {CURRENCY_LIST?.filter(
                        (currency) => currency.code === curr.currencyCode
                      )?.[0]?.currency || curr.currencyCode}
                    </div>
                  </div>
                )
                isoCode = countryList?.filter(
                  (country) => country.currency === curr.currencyCode
                )?.[0]?.isoCode
                constructedData.locale = isoCode
                  ? `${currentLanguage.value || 'en'}-${isoCode}`
                  : 'en-US'
                return constructedData
              }
            )

            setCurrentCurrency(isCurrenyExist ? companyInfo?.currency : 'USD')
            let formatLocale = companyInfo?.language?.includes('_')
              ? `${
                  isCurrenyExist ? companyInfo.language.split('_')[0] : 'en'
                }-${isoCode}`
              : `${
                  isCurrenyExist ? companyInfo?.language || 'en' : 'en'
                }-${isoCode}`
            setCurrentLocale(formatLocale || 'en-US')
            setCurrencyList(updatedCurrencies)
          }
        }
      )
    }

    const showIntercom = () => {
      if (
        match.path === '/buyplans' &&
        document.getElementsByClassName('intercom-lightweight-app')?.[0]
      ) {
        document.getElementsByClassName(
          'intercom-lightweight-app'
        )[0].style.visibility = 'visible'
      }
    }

    const handleCurrencyChange = (currencyObj) => {
      setCurrentCurrency(currencyObj.currencyCode)
      setCurrentLocale(currencyObj.locale)
    }

    useEffect(() => {
      showIntercom()
      getAllFeatures()
      getAllPricingPlans()
      getCurrencies()
      getPricingMeta()
      getAllAddons()
      return function cleanup () {
        if (
          user.chargeBeePlanId === 'solo' &&
          document.getElementsByClassName('intercom-lightweight-app')?.[0]
        )
          document.getElementsByClassName(
            'intercom-lightweight-app'
          )[0].style.visibility = 'hidden'
      }
    }, [])

    const providerValue = {
      tenure,
      setTenure,
      setSelectedPlan,
      handleCurrencyChange,
      currencyList,
      currentCurrency
    }

    const getPriceValue = (planPrices) => {
      const priceVal = planPrices.filter(
        (ele) =>
          ele.currency.currencyCode === currentCurrency &&
          ele.duration === tenure &&
          ele.durationValue === 1
      )?.[0]?.priceValue
      return tenure === 'YEARLY' ? priceVal / 1200 : priceVal / 100
    }

    return (
      <PricingPageContext.Provider value={providerValue}>
        <>
          {!selectedPlan ? (
            isMSPProduct ? (
              <Suspense fallback={<i>loading...</i>}>
                <SuperPricing
                  isNewPricingStructure
                  endpointsPrice={addonPrices}
                  availablePlans={availablePlans}
                  currentCurrency={currentCurrency}
                  currencyList={currencyList}
                  MSPPricingData={pricingMeta}
                  actions={{
                    onSelectPlan: (data) => {
                      const selectedPlan = availablePlans.find(
                        (item) => item.planName === data
                      )
                      const isTieredModel =
                        selectedPlan.planPrices?.[0]?.pricingModel === 'TIERED'

                      setIsTieredModel(isTieredModel)
                      providerValue.setSelectedPlan(selectedPlan)

                      useActivityTracker({
                        event: 'Inproduct purchase',
                        data: {
                          source: 'B. select the plan in pricing page',
                          plan: data
                        }
                      })
                    },
                    onTenureChange: (tenure) => {
                      if (tenure) {
                        setTenure('MONTHLY')
                      } else {
                        setTenure('YEARLY')
                      }
                    },
                    onCurrencyChange: (currency) => {
                      providerValue.handleCurrencyChange(currency)
                    },
                    handleClose: () => {
                      handleClose(false)
                    }
                  }}
                />
              </Suspense>
            ) : (
              <Suspense fallback={<i>loading...</i>}>
                <ITPricing
                  currentCurrency={currentCurrency}
                  currencyList={currencyList}
                  availablePlans={availablePlans}
                  itPricingData={pricingMeta}
                  actions={{
                    onSelectPlan: (data) => {
                      providerValue.setSelectedPlan(
                        availablePlans.find((item) => item.planName === data)
                      )
                      useActivityTracker({
                        event: 'Inproduct IT purchase',
                        data: {
                          source: 'B. select the plan in pricing page',
                          plan: data
                        }
                      })
                    },
                    onTenureChange: (tenure) => {
                      if (tenure) {
                        setTenure('MONTHLY')
                      } else {
                        setTenure('YEARLY')
                      }
                    },
                    onCurrencyChange: (currency) => {
                      providerValue.handleCurrencyChange(currency)
                    },
                    handleClose: () => {
                      handleClose(false)
                    }
                  }}
                />
              </Suspense>
            )
          ) : isMSPProduct && !isTieredModel ? (
            <>
              {!openManageTechModal && !openManageAssetModal && (
                <Page
                  variant='secondary'
                  divider
                  fullWidth
                  contentHeight='100px'
                  title={getTranslatedText(
                    intl,
                    isTrailExpired
                      ? 'app.errorCode.402'
                      : 'app.pricing.purchase.button'
                  )}
                  primaryAction={
                    !isTrailExpired && (
                      <Button plain onClick={() => handleClose(false)}>
                        <SVGIcon name='Close' />
                      </Button>
                    )
                  }
                >
                  <div className='plan-container fh'>
                    <PlanWrapper className='fh'>
                      <Card
                        padding='sizeSmall'
                        AdaptHeight={!isEmpty(selectedPlan)}
                      >
                        {selectedPlan && (
                          <PricingContainer
                            selectedPlan={selectedPlan}
                            allFeatures={pricingMeta?.featureList}
                            planFeatures={pricingMeta?.planProductFeatures}
                            subscriptionTypes={pricingMeta?.subscriptionTypes}
                            tenure={tenure}
                            setTenure={setTenure}
                            setSelectedPlan={setSelectedPlan}
                            currency={currentCurrency}
                            locale={currentLocale}
                            handleManageModal={handleManageModal}
                            isNew
                          />
                        )}
                      </Card>
                    </PlanWrapper>
                  </div>
                </Page>
              )}
              {openManageTechModal && (
                <ModifyTechnician closePage={handleManageModal} />
              )}
              {openManageAssetModal && (
                <ManageAssetModal closePage={handleManageModal} />
              )}
            </>
          ) : (
            <ItPlanPurchase
              isTrailExpired={isTrailExpired}
              selectedPlan={selectedPlan}
              tenure={tenure}
              setTenure={setTenure}
              currency={currentCurrency}
              handleClose={handleClose}
              locale={currentLocale}
              setSelectedPlan={setSelectedPlan}
              isTieredModel={isTieredModel}
              subscriptionTypes={pricingMeta?.subscriptionTypes}
              allFeatures={pricingMeta?.featureList}
            />
          )}
        </>
      </PricingPageContext.Provider>
    )
  }
)

export default withRouter(PricingPage)
